import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { Logo } from '#features/layout/components/Header/Logo';
import { Link } from '#features/routing';
import { staticPageHead } from '#features/static-pages/utils/head';
import { Button, Loading, Panel } from '#features/ui';
import { Switch } from '#features/ui/components/inputs/Switch';

const title = 'Unsubscribe | Teamplay.gg';
const description = 'Manage Teamplay emails.';

const STATUS_PATH = '/notification/api/v1/settings/unsubscribe/status/';
const UNSUBSCRIBE_PATH = '/notification/api/v1/settings/unsubscribe/one_click/';
const RESUBSCRIBE_PATH = '/notification/api/v1/settings/resubscribe/one_click/';

type Scope = 'marketing' | 'activity';
type Subscriptions = { activity: boolean; marketing: boolean };

/**
 * Страница управления письмами, на которую ведёт ссылка из футера письма. Стоит вне `_layout`:
 * человек приходит сюда из почты, скорее всего без сессии, и никаких авторизованных запросов
 * страница делать не должна — всё, что у неё есть, это подписанный токен из ссылки.
 *
 * Показываем оба переключателя, а не одну кнопку «отписаться»: пришедший сюда разбирается с
 * письмами, и «выключить рассылки, оставить ответы на свои посты» — самый частый исход. Раньше
 * страница умела выключить только то, из какого письма пришли, а состояние второй подписки
 * не показывала вовсе.
 *
 * Менять подписки может только POST. Ссылки в письмах прогревают линк-сканеры (корпоративные
 * фильтры, антивирусы, предпросмотр), и отписка по факту перехода срабатывала бы без человека —
 * RFC 8058 ровно поэтому требует POST. GET на one-click бэкенд только редиректит сюда, а статус
 * читается отдельным GET, который ничего не меняет.
 */
export const Route = createFileRoute('/{-$locale}/unsubscribe')({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    t: typeof search.t === 'string' ? search.t : '',
    scope: (search.scope === 'activity' ? 'activity' : 'marketing') as Scope,
  }),
  head: () => staticPageHead({ title, description, pathname: '/unsubscribe', noindex: true }),
});

function UnsubscribePage() {
  const { t } = useLingui();
  const { t: token } = Route.useSearch();

  const [subscriptions, setSubscriptions] = useState<Subscriptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setFailed(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(`${STATUS_PATH}${encodeURIComponent(token)}`);
        if (!response.ok) {
          throw new Error('status');
        }
        const data = (await response.json()) as Subscriptions;
        if (!cancelled) {
          setSubscriptions({ activity: data.activity, marketing: data.marketing });
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const apply = useCallback(
    async (target: Scope | 'all', nextEnabled: boolean) => {
      if (!token) {
        return;
      }
      setPending(true);
      // nginx-локации one-click не пробрасывают query string, поэтому параметры едут form-телом.
      const body = target === 'all' ? 'all=true' : `scope=${encodeURIComponent(target)}`;
      try {
        const response = await fetch(
          `${nextEnabled ? RESUBSCRIBE_PATH : UNSUBSCRIBE_PATH}${encodeURIComponent(token)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
          }
        );
        if (!response.ok) {
          throw new Error('apply');
        }
        setSubscriptions((prev) => {
          if (!prev) {
            return prev;
          }
          if (target === 'all') {
            return { activity: nextEnabled, marketing: nextEnabled };
          }
          return { ...prev, [target]: nextEnabled };
        });
      } catch {
        setFailed(true);
      } finally {
        setPending(false);
      }
    },
    [token]
  );

  const allOff = subscriptions ? !subscriptions.activity && !subscriptions.marketing : false;

  return (
    <Flex
      minH="100dvh"
      direction="column"
      gap={8}
      alignItems="center"
      justifyContent="center"
      px={4}
      py={8}
    >
      <Link to="/{-$locale}" aria-label="Teamplay">
        <Logo />
      </Link>

      <Panel
        variant="object"
        display="flex"
        flexDir="column"
        gap={5}
        alignItems="stretch"
        p={{ base: 6, md: 10 }}
        maxW="480px"
        w="100%"
      >
        <Stack gap={2} textAlign="center">
          <Heading textStyle="heading">
            {t`Email settings`}
          </Heading>
          <Text color="fg.muted">
            {failed
              ? t`We couldn't process this link. Open notification settings in your profile to manage emails.`
              : t`Choose which emails you want to receive. Changes apply right away.`}
          </Text>
        </Stack>

        {loading && (
          <Flex justify="center" py={4}>
            <Loading variant="inline" />
          </Flex>
        )}

        {!loading && !failed && subscriptions && (
          <>
            <Panel variant="nested" display="flex" flexDir="column" gap={4} p={4}>
              <Text textStyle="title">{t`Email`}</Text>

              <Flex gap={4} align="center" justify="space-between">
                <Stack gap={1} flex={1}>
                  <Text textStyle="title">{t`Activity`}</Text>
                  <Text textStyle="caption" color="fg.muted">
                    {t`Replies, invites, session updates and chat messages.`}
                  </Text>
                </Stack>
                <Switch
                  colorPalette="purple"
                  size="md"
                  name="activity"
                  value={subscriptions.activity}
                  onChange={() => void apply('activity', !subscriptions.activity)}
                  readOnly={pending}
                />
              </Flex>

              <Box borderTopWidth="1px" borderColor="border" />

              <Flex gap={4} align="center" justify="space-between">
                <Stack gap={1} flex={1}>
                  <Text textStyle="title">{t`Newsletters and digests`}</Text>
                  <Text textStyle="caption" color="fg.muted">
                    {t`Product news and weekly digests.`}
                  </Text>
                </Stack>
                <Switch
                  colorPalette="purple"
                  size="md"
                  name="marketing"
                  value={subscriptions.marketing}
                  onChange={() => void apply('marketing', !subscriptions.marketing)}
                  readOnly={pending}
                />
              </Flex>
            </Panel>

            {allOff ? (
              <Text textStyle="caption" color="fg.muted" textAlign="center">
                {t`We won't send you any more emails, except essential account messages like password resets.`}
              </Text>
            ) : (
              <Button variant="outline" disabled={pending} onClick={() => void apply('all', false)}>
                {t`Turn off all emails`}
              </Button>
            )}
          </>
        )}

        <Button asChild variant="ghost">
          <Link to="/{-$locale}">{t`Go to home`}</Link>
        </Button>
      </Panel>
    </Flex>
  );
}
